export const routes = {
  accessDenied: (): string => "/access-denied",

  admin: {
    adminDashboard: (): string => "/admin/admin-dashboard",
    caseDashboard: (): string => "/admin/case-dashboard",
    caseworkers: (): string => "/admin/caseworkers",
    communications: (): string => "/admin/communications",
    coordinatorDashboard: (): string => "/admin/coordinator-dashboard",
    cReports: (): string => "/admin/c-reports",
    dashboard: (): string => "/admin/dashboard",
    home: (): string => "/admin",
    need: (needId: string): string => `/admin/needs/${needId}`,
    needEdit: (needId: string): string => `/admin/needs/${needId}/edit`,
    needNew: (): string => "/admin/needs/new",
    needs: (): string => "/admin/needs",
    // New: filter needs by case worker
    needsForCaseWorker: (caseWorkerId: string): string =>
      `/admin/needs?caseWorkerId=${encodeURIComponent(caseWorkerId)}`,

    needsPendingApproval: (): string => "/admin/needs/pending-approval",
    offer: (offerId: string): string => `/admin/offers/${offerId}`,
    offers: (): string => "/admin/offers",
    reports: (): string => "/admin/reports",
    reportsImpact: (): string => "/admin/reports/impact",
    reportsNeedsAnalytics: (): string => "/admin/reports/needs-analytics",
    reportsPerformance: (): string => "/admin/reports/performance",
    reportsTrends: (): string => "/admin/reports/trends",
    reportsVolunteerEngagement: (): string =>
      "/admin/reports/volunteer-engagement",
    settings: (): string => "/admin/settings",
    settingsApiKeys: (): string => "/admin/settings/api-keys",
    settingsAreas: (): string => "/admin/settings/areas",
    settingsBilling: (): string => "/admin/settings/billing",
    settingsBranding: (): string => "/admin/settings/branding",
    settingsCategories: (): string => "/admin/settings/categories",
    settingsDataExport: (): string => "/admin/settings/data-export",
    settingsEmbed: (): string => "/admin/settings/embed",
    settingsIntegrations: (): string => "/admin/settings/integrations",
    settingsLanguage: (): string => "/admin/settings/language",
    settingsProfile: (): string => "/admin/settings/profile",
    settingsSecurity: (): string => "/admin/settings/security",
    settingsTags: (): string => "/admin/settings/tags",
    settingsWebhooks: (): string => "/admin/settings/webhooks",
    stories: (): string => "/admin/stories",
    taxReceipts: (): string => "/admin/tax-receipts",
    userEdit: (userId: string): string => `/admin/users/${userId}/edit`,
    userInvite: (): string => "/admin/users/invite",
    users: (): string => "/admin/users",
    usersPending: (): string => "/admin/users?tab=pending",
    usersWithRole: (role: string): string =>
      `/admin/users?role=${encodeURIComponent(role)}`,
    volunteer: (volunteerId: string): string =>
      `/admin/volunteers/${volunteerId}`,
    volunteerEdit: (volunteerId: string): string =>
      `/admin/volunteers/${volunteerId}/edit`,
    volunteerNew: (): string => "/admin/volunteers/new",
    volunteers: (): string => "/admin/volunteers",
  },

  api: {
    admin: {
      exportNeeds: (query?: string): string =>
        `/api/admin/needs/export${query ? `?${query}` : ""}`,
      exportUsers: (query?: string): string =>
        `/api/admin/users/export${query ? `?${query}` : ""}`,
      exportVolunteers: (query?: string): string =>
        `/api/admin/volunteers/export${query ? `?${query}` : ""}`,
    },
    unsubscribe: {
      oneClick: (token: string): string =>
        `/api/unsubscribe/one-click?token=${encodeURIComponent(token)}`,
    },
  },

  auth: {
    acceptInvite: (): string => "/accept-invite",
    forgotPassword: (): string => "/forgot-password",
    login: (): string => "/login",
    register: (): string => "/register",
    registrationSubmitted: (): string => "/registration-submitted",
    resendVerification: (email?: string): string =>
      email
        ? `/resend-verification?email=${encodeURIComponent(email)}`
        : "/resend-verification",
    resetPassword: (): string => "/reset-password",
    verifyEmail: (): string => "/verify-email",
  },

  docs: {
    caseWorkerTraining: (): string => "/docs/roles/case-worker",
    // Deprecated: prefer the named helper above for stability
    caseworkerTraining: (): string => "/docs/roles/case-worker",
    home: (): string => "/docs",
  },

  org: {
    area: (orgSlug: string, area: string): string =>
      `/${orgSlug}/areas/${area}`,
    areas: (orgSlug: string): string => `/${orgSlug}/areas`,
    billingSuccess: (orgSlug: string): string => `/${orgSlug}/billing/success`,
    claim: (orgSlug: string, needId: string): string =>
      `/${orgSlug}/needs/${needId}/claim`,
    claimDetails: (orgSlug: string, needId: string, token: string): string =>
      `/${orgSlug}/needs/${needId}/claim/${token}`,
    claimed: (orgSlug: string, needId: string): string =>
      `/${orgSlug}/needs/${needId}/claimed`,
    home: (orgSlug: string): string => `/${orgSlug}`,
    need: (orgSlug: string, needId: string): string =>
      `/${orgSlug}/needs/${needId}`,
    needs: (orgSlug: string): string => `/${orgSlug}/needs`,
    offerDonation: (orgSlug: string): string => `/${orgSlug}/offer-donation`,
    offerThankYou: (orgSlug: string): string =>
      `/${orgSlug}/offer-donation/thank-you`,
    registerCaseWorker: (orgSlug: string): string =>
      `/${orgSlug}/register-case-worker`,
    stories: (orgSlug: string): string => `/${orgSlug}/stories`,
    story: (orgSlug: string, needId: string): string =>
      `/${orgSlug}/needs/${needId}/story`,
    storyDetails: (orgSlug: string, storyId: string): string =>
      `/${orgSlug}/stories/${storyId}`,
    volunteerSignup: (orgSlug: string): string =>
      `/${orgSlug}/volunteer/signup`,
    volunteerThankYou: (orgSlug: string): string =>
      `/${orgSlug}/volunteer/thank-you`,
  },

  preferences: {
    manage: (token: string): string => `/preferences/${token}`,
  },

  public: {
    about: (): string => "/about",
    availableNeeds: (): string => "/available-needs",
    cafo: () => "/cafo",
    contact: (): string => "/contact",
    contactWithTopic: (topic: string): string =>
      `/contact?topic=${encodeURIComponent(topic)}`,
    demo: (): string => "/demo",
    docsHome: (): string => "/docs",
    /** @deprecated Prefer explicit `/docs/...` paths; Help Scout categories do not map 1:1. */
    docsPage: (category: string, slug: string, subcategory?: string): string =>
      subcategory
        ? `/docs/${category}/${subcategory}/${slug}`
        : `/docs/${category}/${slug}`,
    emailDemo: (template: string): string => `/email-demos/${template}`,
    emailDemos: (): string => "/email-demos",
    features: (): string => "/features",
    home: (): string => "/",
    howItWorks: (): string => "/how-it-works",
    pricing: (): string => "/pricing",
    useCases: (): string => "/use-cases",
  },

  superAdmin: {
    activity: (): string => "/super-admin/activity",
    dashboard: (): string => "/super-admin/dashboard",
    home: (): string => "/super-admin",
    orgDetail: (orgId: string): string => `/super-admin/orgs/${orgId}`,
  },

  support: {
    /** Contact form; submissions email ADMIN_EMAIL, which routes to Help Scout. */
    createTicket: (): string => "/contact",
    /** Self-hosted docs under `app/(public)/docs`, sourced from `docs/content/`. */
    helpCenter: (): string => "/docs",
  },

  unsubscribe: {
    manage: (token: string): string => `/unsubscribe/${token}`,
  },
};
